import {
	usePreviewVetterPriceRequestQuery,
	useSendVetterPriceRequestMutation,
} from '@codezo/graphql';
import { ArrowUpRight, Check, Copy, Info, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { VetterSelector } from '../../selectors/VetterSelector';
import { Button } from '../../ui/button';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogForm,
	DialogHeader,
	DialogTitle,
} from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { QuoteLinePicker, type QuoteLine } from './QuoteLinePicker';
import { EVERYONE, VetterRecipientPicker } from './VetterRecipientPicker';

interface SendPriceRequestModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	orderQuoteId: string;
	lines: QuoteLine[];
	onSuccess: () => void;
}

/**
 * Asks one supplier to price chosen quote lines.
 *
 * Nobody is typed in here: a supplier's people are managed on the supplier's
 * own page. "Send to" is one of them, or — when the supplier has account
 * holders — everyone with an account (the default; no recipient is sent, and
 * the backend emails them all). A supplier with nobody on the platform can't
 * be sent a request: add someone on the supplier's page first.
 *
 * A supplier has one request per quote; sending again updates its products
 * and keeps prices already sent. All of this comes from
 * `previewVetterPriceRequest` — the form only chooses.
 */
export function SendPriceRequestModal({
	open,
	onOpenChange,
	orderQuoteId,
	lines,
	onSuccess,
}: SendPriceRequestModalProps) {
	const [vetterEntityId, setVetterEntityId] = useState('');
	/** EVERYONE, or the vetterUserId of the one person it's sent to. */
	const [recipient, setRecipient] = useState('');
	const [itemIds, setItemIds] = useState<string[]>([]);
	/** The supplier the form's defaults were last taken from. */
	const [defaultsFor, setDefaultsFor] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [sentLink, setSentLink] = useState<{ link: string; email: string } | null>(null);
	const [copied, setCopied] = useState(false);

	const [send, { loading: sending }] = useSendVetterPriceRequestMutation();

	// Reset on each opening, during render so the first frame is already clean.
	const [wasOpen, setWasOpen] = useState(false);
	if (open !== wasOpen) {
		setWasOpen(open);
		if (open) {
			setVetterEntityId('');
			setRecipient('');
			setItemIds([]);
			setDefaultsFor(null);
			setError(null);
			setSentLink(null);
			setCopied(false);
		}
	}

	const { data: previewData, loading: previewLoading } = usePreviewVetterPriceRequestQuery({
		variables: { orderQuoteId, vetterEntityId },
		fetchPolicy: 'no-cache',
		skip: !open || !vetterEntityId,
	});
	const preview = previewData?.previewVetterPriceRequest;
	const previewReady = !!vetterEntityId && !previewLoading && !!preview;

	const recipients = preview?.recipients ?? [];
	const accountHolders = recipients.filter((r) => r.hasAccount);
	const hasAccounts = !!preview?.hasAccountHolders;
	const nobodyToReach = previewReady && !hasAccounts && recipients.length === 0;

	/* Defaults, once per supplier: their existing request's products (else every
	   line), and the first of their people when a link is needed. */
	useEffect(() => {
		if (!previewReady || !preview || defaultsFor === vetterEntityId) return;
		setDefaultsFor(vetterEntityId);
		setItemIds(preview.existingJob?.orderQuoteItemIds ?? lines.map((l) => l.id));
		setRecipient(
			preview.hasAccountHolders ? EVERYONE : (preview.recipients[0]?.vetterUserId ?? '')
		);
	}, [previewReady, preview, defaultsFor, vetterEntityId, lines]);

	const valid =
		previewReady &&
		!nobodyToReach &&
		(recipient === EVERYONE ? hasAccounts : !!recipient) &&
		itemIds.length > 0;

	const handleSubmit = async () => {
		if (!valid || sending) return;
		setError(null);
		try {
			const { data } = await send({
				variables: {
					input: {
						orderQuoteId,
						vetterEntityId,
						orderQuoteItemIds: itemIds,
						// No recipient means every account holder is emailed.
						recipientVetterUserId: recipient === EVERYONE ? undefined : recipient,
					},
				},
			});
			const res = data?.sendVetterPriceRequest;
			// Failures come back as `success: false` with a ready-to-show reason.
			if (!res?.success) {
				setError(res?.message || 'Failed to send the price request.');
				return;
			}
			toast.success(res.message || 'Price request sent');
			onSuccess();
			// A link comes back only when the person was reached by link.
			if (res.link) setSentLink({ link: res.link, email: res.email ?? '' });
			else onOpenChange(false);
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to send the price request.');
		}
	};

	const handleCopy = async () => {
		if (!sentLink) return;
		try {
			await navigator.clipboard.writeText(sentLink.link);
			setCopied(true);
			setTimeout(() => setCopied(false), 1500);
		} catch {
			toast.error('Could not copy the link');
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>Request supplier prices</DialogTitle>
					<DialogDescription>
						The supplier enters cost, production time and shipping for the products you
						pick.
					</DialogDescription>
				</DialogHeader>

				{sentLink ? (
					<>
						<DialogBody>
							<div className="flex flex-col gap-2">
								<div className="text-sm text-text-2">
									Sent to <span className="text-text-1">{sentLink.email}</span> by
									secure link. You can also share it directly:
								</div>
								<div className="flex items-center gap-2">
									<Input
										readOnly
										value={sentLink.link}
										className="font-mono text-xs"
									/>
									<Button variant="outline" onClick={handleCopy}>
										{copied ? (
											<Check size={14} data-icon="inline-start" />
										) : (
											<Copy size={14} data-icon="inline-start" />
										)}
										{copied ? 'Copied' : 'Copy'}
									</Button>
								</div>
							</div>
						</DialogBody>
						<DialogFooter>
							<Button onClick={() => onOpenChange(false)}>Done</Button>
						</DialogFooter>
					</>
				) : (
					<DialogForm onSubmit={handleSubmit}>
						<DialogBody>
							<div className="flex flex-col gap-4">
								<div className="flex flex-col gap-1.5">
									<Label>
										Supplier <span className="text-destructive">*</span>
									</Label>
									<VetterSelector
										value={vetterEntityId}
										onChange={(id) => {
											setVetterEntityId(id);
											setError(null);
										}}
										placeholder="Choose a supplier"
										creatable
									/>
								</div>

								{vetterEntityId && !previewReady && (
									<div className="flex items-center gap-2 text-sm text-text-4">
										<Loader2 size={13} className="animate-spin" />
										Loading…
									</div>
								)}

								{previewReady && preview?.notice && (
									<Notice icon={<Info size={14} />}>{preview.notice}</Notice>
								)}

								{previewReady && !nobodyToReach && (
									<div className="flex flex-col gap-1.5">
										<Label>
											Send to <span className="text-destructive">*</span>
										</Label>
										<VetterRecipientPicker
											people={recipients}
											everyoneLabel={
												hasAccounts
													? {
															title: 'Everyone with an account',
															sub:
																accountHolders.length > 0
																	? `Emails ${accountHolders.map((a) => a.name).join(', ')} · sees it in their portal`
																	: 'Emails everyone with an account · sees it in their portal',
														}
													: null
											}
											value={recipient}
											onChange={(v) => {
												setRecipient(v);
												if (error) setError(null);
											}}
											disabled={sending}
										/>
										{/* Only true of one person: say what the rest still get. */}
										{hasAccounts && recipient !== EVERYONE && (
											<div className="text-sm text-text-4">
												Only this person is emailed. Everyone else with an
												account still sees it in their portal.
											</div>
										)}
									</div>
								)}

								{/* Disabled with the reason, not hidden: say where to fix it. */}
								{nobodyToReach && (
									<div className="flex flex-col gap-1.5 rounded-[var(--r-3)] border border-line-1 bg-surface-sunken px-3 py-2.5 text-sm">
										<span className="text-text-1">
											Nobody at this supplier is on the platform yet.
										</span>
										<span className="text-text-3">
											Add someone on the supplier&apos;s page, then send the
											request.
										</span>
										<Link
											to={`/vetters/${vetterEntityId}`}
											className="flex items-center gap-1 self-start text-text-2 underline-offset-2 hover:text-text-1 hover:underline"
										>
											Open supplier
											<ArrowUpRight size={12} />
										</Link>
									</div>
								)}

								{/* Once the supplier is known: their existing request decides what
								    starts ticked. */}
								{previewReady && !nobodyToReach && (
									<div className="flex flex-col gap-1.5">
										<Label>
											Products to price{' '}
											<span className="text-destructive">*</span>
										</Label>
										<QuoteLinePicker
											lines={lines}
											value={itemIds}
											onChange={setItemIds}
											disabled={sending}
										/>
									</div>
								)}

								{error && <div className="text-sm text-status-danger">{error}</div>}
							</div>
						</DialogBody>

						<DialogFooter>
							<Button
								type="button"
								variant="outline"
								onClick={() => onOpenChange(false)}
								disabled={sending}
							>
								Cancel
							</Button>
							<Button type="submit" disabled={!valid || sending}>
								{sending
									? 'Sending…'
									: preview?.existingJob
										? 'Update request'
										: 'Send request'}
							</Button>
						</DialogFooter>
					</DialogForm>
				)}
			</DialogContent>
		</Dialog>
	);
}

function Notice({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
	return (
		<div className="flex items-start gap-2 rounded-[var(--r-3)] border border-line-1 bg-surface-sunken px-3 py-2 text-sm text-text-2">
			<span className="mt-0.5 shrink-0 text-text-3">{icon}</span>
			<span>{children}</span>
		</div>
	);
}
